/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NetworkSecurityPolicyModule
 */

import {
    Component,
    Inject,
    Input,
    OnInit,
} from '@angular/core';

import { ClrFormLayout } from '@clr/angular';
import { L10nService } from '@vmw/ngx-vip';

import {
    NetworkSecurityPolicyItem,
    NetworkSecurityRuleConfigItem,
} from 'ajs/modules/network-security-policy/factories';

import { IRowReorder } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import { IPReputationDBCollection } from 'ajs/modules/ip-reputation-db';
import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { GeoDbCollection } from 'ajs/modules/geo-db';

import * as l10n from './network-security-policy-modal.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TIPReputationDBCollection = typeof IPReputationDBCollection;
type TGeoDbCollection = typeof GeoDbCollection;

/**
 * @description Modal component for Network Security Policy ObjectTypeItem.
 *
 * @author Abhinesh Gour
 */
@Component({
    selector: 'network-security-policy-modal',
    templateUrl: './network-security-policy-modal.component.html',
})
export class NetworkSecurityPolicyModalComponent implements OnInit {
    /**
     * NetworkSecurityPolicy ObjectTypeItem instance.
     */
    @Input()
    public editable: NetworkSecurityPolicyItem;

    /**
     * NetworkSecurityPolicy modal clr-form layout.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * For template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * NetworkSecurityPolicy objectType.
     */
    public objectType: string;

    /**
     * IPReputationDBCollection instance.
     */
    public readonly ipReputationDBCollection: IPReputationDBCollection;

    /**
     * GeoDbCollection instance.
     */
    public readonly geoDbCollection: GeoDbCollection;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(IPReputationDBCollection)
        IPReputationDBCollection: TIPReputationDBCollection,
        @Inject(GeoDbCollection)
        GeoDbCollection: TGeoDbCollection,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
        this.ipReputationDBCollection = new IPReputationDBCollection();
        this.geoDbCollection = new GeoDbCollection();
    }

    /** @override */
    public ngOnInit(): void {
        this.objectType = this.editable.messageType;
    }

    /**
     * Opens modal to add new Network Security Rule.
     */
    public onAddNetworkSecurityRule(): void {
        this.editable.addNetworkSecurityRule();
    }

    /**
     * Opens modal to edit Network Security Rule.
     */
    public onEditNetworkSecurityRule(rule: NetworkSecurityRuleConfigItem): void {
        this.editable.editNetworkSecurityRule(rule);
    }

    /**
     * Deletes Network Security Rule.
     */
    public onDeleteNetworkSecurityRule(rule: NetworkSecurityRuleConfigItem): void {
        this.editable.rules.removeByMessageItem(rule);
    }

    /**
     * Handles Network Security Rule reorder event.
     * Update array index as well as `index` property.
     */
    public handleNetworkSecurityRuleGridRowReorder(
        { previousIndex, currentIndex }: IRowReorder,
    ): void {
        this.editable.rules.moveByIndex(previousIndex, currentIndex);
    }

    /** @override */
    public ngOnDestroy(): void {
        [
            this.ipReputationDBCollection,
            this.geoDbCollection,
        ].forEach((collection: Collection) => collection.destroy());
    }
}
